<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { CheckCircle2, Link2, Link2Off, RefreshCw } from 'lucide-vue-next';
import { ref } from 'vue';
import * as zoteroRoutes from '@/routes/zotero';
const { zotero } = defineProps<{
    zotero: {
        connected: boolean;
        zotero_user_id?: string;
        connected_at?: string;
        last_synced_at?: string;
    };
}>();

const emit = defineEmits<{
    import: [];
}>();

const connecting = ref(false);
const disconnecting = ref(false);

async function connect() {
    connecting.value = true;

    try {
        const res = await fetch('/integrations/zotero/auth/url');
        const data = await res.json();

        if (data.url) {
            window.location.href = data.url;
        }
    } finally {
        connecting.value = false;
    }
}

function disconnect() {
    disconnecting.value = true;
    router.post(
        zoteroRoutes.disconnect().url,
        {},
        { onFinish: () => (disconnecting.value = false) },
    );
}

function formatDate(iso?: string) {
    if (!iso) {
        return null;
    }

    return new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}
</script>

<template>
    <div
        data-tour="zotero-connect-card"
        class="rounded-lg border border-border p-6"
    >
        <!-- Header -->
        <div class="mb-3 flex items-center justify-between">
            <h2 class="text-sm font-semibold">Depuis Zotero</h2>
            <span
                v-if="zotero.connected"
                class="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
            >
                <CheckCircle2 class="h-3 w-3" />
                Connecté
            </span>
            <span
                v-else
                class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
                Non connecté
            </span>
        </div>

        <!-- Connected state -->
        <template v-if="zotero.connected">
            <div class="mb-4 space-y-1.5 text-xs text-muted-foreground">
                <div class="flex items-center justify-between">
                    <span>ID Zotero</span>
                    <span class="font-mono text-foreground">{{
                        zotero.zotero_user_id
                    }}</span>
                </div>
                <div
                    v-if="zotero.last_synced_at"
                    class="flex items-center justify-between"
                >
                    <span>Dernière synchro</span>
                    <span class="text-foreground">{{
                        formatDate(zotero.last_synced_at)
                    }}</span>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <button
                    class="flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:opacity-90 disabled:opacity-50"
                    @click="emit('import')"
                >
                    <RefreshCw class="h-3.5 w-3.5" />
                    Importer dans ce projet
                </button>
                <button
                    class="flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground transition hover:border-rose-300 hover:text-rose-600 disabled:opacity-50"
                    :disabled="disconnecting"
                    @click="disconnect"
                >
                    <Link2Off class="h-3.5 w-3.5" />
                    Déconnecter Zotero
                </button>
            </div>
        </template>

        <!-- Disconnected state -->
        <template v-else>
            <p class="mb-4 text-xs text-muted-foreground">
                Synchronisez votre bibliothèque Zotero pour importer
                automatiquement vos références dans ce projet.
            </p>
            <button
                class="flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:opacity-90 disabled:opacity-50"
                :disabled="connecting"
                @click="connect"
            >
                <Link2 class="h-3.5 w-3.5" />
                {{ connecting ? 'Redirection…' : 'Connecter Zotero' }}
            </button>
        </template>
    </div>
</template>
